import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { History, Sparkles, Trash2 } from 'lucide-react'
import { api, ApiError } from '../../api/client'
import { Button } from '../../components/ui/Button'
import { PageHeader } from '../../components/ui/PageHeader'
import { SectionedMarkdown } from '../../components/ui/SectionedMarkdown'
import { usePaywallStore } from '../../store/paywall.store'

/**
 * Dream-decode form: free-form textarea → AI returns a tarot-flavoured reading
 * tinted by the current moon phase. Premium-gated (server-side); on 402 we
 * pop the standard PaywallModal.
 */
export function LunaDreamsRoute() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const showPaywall = usePaywallStore((s) => s.show)
  const [text, setText] = useState('')

  const decodeMut = useMutation({
    mutationFn: () => api.dreamDecode(text),
    onSuccess: (dream) => {
      // Land the user on the dedicated reading page so the history flow is
      // identical to "open from list".
      navigate(`/luna/dreams/${dream.id}`, { replace: true })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        showPaywall({ reason: 'feature_locked', message: t('dreams.locked') })
        return
      }
      const msg = err instanceof ApiError ? err.body.error.message : (err as Error).message
      toast.error(msg)
    },
  })

  const trimmed = text.trim()
  const canSubmit = trimmed.length >= 10

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={t('dreams.title')}
        subtitle={t('dreams.intro')}
        backTo="/luna"
        right={
          <Link
            to="/luna/dreams/history"
            aria-label={t('dreams.history_link')}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-sage-100/70 text-sage-700 transition hover:bg-sage-200/80 active:scale-[0.95]"
          >
            <History className="h-4 w-4" strokeWidth={1.8} aria-hidden />
          </Link>
        }
      />

      <section className="flex flex-col gap-3 rounded-3xl bg-white/70 p-4 ring-1 ring-sage-200/60">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-sage-700">{t('dreams.input_label')}</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 2000))}
            placeholder={t('dreams.placeholder')}
            rows={8}
            className="resize-none rounded-2xl border border-sage-200 bg-white px-3 py-2 text-sage-800 outline-none focus:border-sage-500"
          />
          <span className="text-right text-xs text-sage-400">{trimmed.length} / 2000</span>
        </label>

        <Button
          variant="primary"
          size="lg"
          disabled={!canSubmit}
          loading={decodeMut.isPending}
          onClick={() => decodeMut.mutate()}
          leftIcon={<Sparkles className="h-4 w-4" strokeWidth={1.8} aria-hidden />}
        >
          {t('dreams.decode_cta')}
        </Button>
        <p className="text-center text-xs text-sage-500">{t('dreams.disclaimer')}</p>
      </section>
    </div>
  )
}

/** Single decoded dream: full text + AI interpretation + delete. */
export function LunaDreamReadingRoute() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const dreamQ = useQuery({
    queryKey: ['dream', id],
    queryFn: () => api.dreamGet(id!),
    enabled: !!id,
  })

  const deleteMut = useMutation({
    mutationFn: () => api.dreamDelete(id!),
    onSuccess: () => {
      toast.success(t('dreams.deleted'))
      navigate('/luna/dreams/history', { replace: true })
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.body.error.message : (err as Error).message
      toast.error(msg)
    },
  })

  if (dreamQ.isLoading) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <PageHeader title={t('dreams.reading_title')} backTo="/luna/dreams/history" />
        <div className="h-40 animate-pulse rounded-2xl bg-sage-100" />
      </div>
    )
  }
  if (!dreamQ.data) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <PageHeader title={t('dreams.not_found')} backTo="/luna/dreams/history" />
      </div>
    )
  }
  const d = dreamQ.data
  const dt = new Date(d.createdAt)
  const subtitle = [
    dt.toLocaleString('ru-RU', { dateStyle: 'long', timeStyle: 'short' }),
    d.moonPhase && t(`lunar.phase.${d.moonPhase}` as never),
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={t('dreams.reading_title')}
        subtitle={subtitle}
        backTo="/luna/dreams/history"
      />

      <section className="rounded-2xl bg-amber-50 p-3 text-sm italic text-amber-900 ring-1 ring-amber-200">
        «{d.text}»
      </section>

      {/* SectionedMarkdown already wraps each section in its own card with
          ring — an extra outer wrapper here was rendering a double frame. */}
      <SectionedMarkdown>{d.interpretation}</SectionedMarkdown>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          if (window.confirm(t('dreams.confirm_delete'))) deleteMut.mutate()
        }}
        loading={deleteMut.isPending}
        leftIcon={<Trash2 className="h-4 w-4" strokeWidth={1.8} aria-hidden />}
      >
        {t('dreams.delete')}
      </Button>
    </div>
  )
}

/** History list. */
export function LunaDreamsHistoryRoute() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const histQ = useQuery({
    queryKey: ['dreams', 'history'],
    queryFn: () => api.dreamsHistory(),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.dreamDelete(id),
    onSuccess: () => {
      toast.success(t('dreams.deleted'))
      qc.invalidateQueries({ queryKey: ['dreams', 'history'] })
    },
  })
  const items = histQ.data ?? []
  return (
    <div className="flex flex-col gap-4 pb-6">
      <PageHeader title={t('dreams.history_title')} backTo="/luna/dreams" />

      {histQ.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-2xl bg-sage-100" />
          ))}
        </div>
      )}

      {!histQ.isLoading && items.length === 0 && (
        <div className="rounded-3xl bg-white/70 p-6 text-center ring-1 ring-sage-200/60">
          <p className="text-sage-700">{t('dreams.empty')}</p>
          <Button variant="secondary" className="mt-3" onClick={() => navigate('/luna/dreams')}>
            {t('dreams.try_now')}
          </Button>
        </div>
      )}

      {items.map((row) => {
        const dt = new Date(row.createdAt)
        return (
          <button
            key={row.id}
            type="button"
            onClick={() => navigate(`/luna/dreams/${row.id}`)}
            className="flex flex-col gap-1 rounded-2xl bg-white/80 p-3 text-left ring-1 ring-sage-200/60 transition hover:ring-sage-400"
          >
            <p className="line-clamp-2 text-sm text-sage-800">{row.preview}…</p>
            <div className="flex items-center justify-between pt-1 text-xs text-sage-500">
              <span>
                {dt.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
                {row.moonPhase && ` · ${t(`lunar.phase.${row.moonPhase}` as never)}`}
              </span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation()
                  if (window.confirm(t('dreams.confirm_delete'))) deleteMut.mutate(row.id)
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && window.confirm(t('dreams.confirm_delete'))) {
                    e.preventDefault()
                    e.stopPropagation()
                    deleteMut.mutate(row.id)
                  }
                }}
                className="inline-flex items-center gap-1 text-sage-500 hover:text-copper"
              >
                <Trash2 className="h-3 w-3" strokeWidth={1.8} aria-hidden />
              </span>
            </div>
          </button>
        )
      })}
    </div>
  )
}
